import React, { useState, useEffect } from 'react';
import { mockData } from '@/services/mockData';
import BotsPanel from './dashboard/BotsPanel';
import Header from '@/components/Header';
import { config } from '@/config';

interface UserIdentity {
  email: string | null;
  uuid: string | null;
  ts: string;
}

interface SystemStatus {
  bots: number;
  positions: number;
  trades: number;
}

function Dashboard() {
  const [userIdentity, setUserIdentity] = useState<UserIdentity | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      // Try to get real user data from API
      const userRes = await fetch(`${config.apiBase}/me`, {
        credentials: 'include'
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.ok && userData.user) {
          setUserIdentity({
            email: userData.user.email,
            uuid: userData.user.uuid,
            ts: userData.user.ts || new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.log('Using fallback user data');
    }

    try {
      // Try to get real system status from API
      const statusRes = await fetch(`${config.apiBase}/user/status`, {
        credentials: 'include'
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData.ok) {
          setSystemStatus({
            bots: statusData.bots || 0,
            positions: statusData.positions || 0,
            trades: statusData.trades || 0
          });
        }
      }
    } catch (error) {
      console.log('Using fallback system status');
    }

    // Fallback to mock data if API calls fail
    if (!userIdentity) {
      setUserIdentity(mockData.user);
    }
    if (!systemStatus) {
      setSystemStatus({
        bots: mockData.bots.length,
        positions: mockData.positions.length,
        trades: mockData.trades.length
      });
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto p-3 md:p-6">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Dashboard</h1>
            
            {/* User Identity Section */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">User Identity</h2>
              {userIdentity ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Email</label>
                      <p className="text-gray-900">{userIdentity.email || 'Not authenticated'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">User ID</label>
                      <p className="text-gray-900 font-mono text-sm">{userIdentity.uuid || 'Not available'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                      <p className="text-gray-900">{new Date(userIdentity.ts).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800">User identity not available</p>
                </div>
              )}
            </div>

            {/* System Status Section */}
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-3">System Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-blue-600 text-2xl">🤖</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Active Bots</p>
                      <p className="text-2xl font-bold text-blue-900">{systemStatus?.bots || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-green-600 text-2xl">💰</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">Open Positions</p>
                      <p className="text-2xl font-bold text-green-900">{systemStatus?.positions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="text-purple-600 text-2xl">📊</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">Total Trades</p>
                      <p className="text-2xl font-bold text-purple-900">{systemStatus?.trades || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bots Panel */}
            <div className="mt-6">
              <BotsPanel />
            </div>

            {/* Sample Data Preview */}
            <div className="mt-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Recent Activity</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-2">Active Bots</h3>
                  {mockData.bots.map(bot => (
                    <div key={bot.id} className="text-sm text-gray-600 mb-1">
                      {bot.name} - {bot.status}
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-800 mb-2">Open Positions</h3>
                  {mockData.positions.map(pos => (
                    <div key={pos.id} className="text-sm text-gray-600 mb-1">
                      {pos.pair} {pos.side} - PnL: ${pos.pnl}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;